<template>
    <message-hub-sidebar-row
        :active="active"
        :unread="unread"
        :to="{
            name: route.name === 'message-hub.contact-view' ? 'message-hub.contact-view' : 'message-hub.conversation',
            params: {
                contactId: props.thread.contact_id,
            },
            query: {
                ...route.query,
                inbox: currentInbox,
                group: currentOrgUnit && props.thread.group_id ? props.thread.group_id : undefined,
            },
        }"
        :class="{ 'select-none': props.bulkSelection }"
        :selected="!props.bulkSelection ? undefined : isThreadSelected(props.thread)"
    >
        <template v-slot:title>
            {{
                props.thread.contact_name ||
                (props.thread.contact_phone ? formatPhoneNumber(props.thread.contact_phone) : 'Deleted Contact')
            }}
        </template>

        <template v-slot:date>
            <span :key="dateRefreshKey"> {{ formattedDate() }} </span>
        </template>

        <template v-slot:content>
            {{ props.thread.last_message_content }}
        </template>

        <template v-slot:afterContent><slot name="afterContent"></slot></template>

        <template v-if="unread || assignee !== undefined || showGroupBadge" v-slot:badges>
            <vx-badge v-if="unread" size="xs" class="whitespace-nowrap">Unread</vx-badge>

            <vx-badge
                v-if="assignee !== undefined"
                size="xs"
                :color="active ? 'primary' : 'slate'"
                class="whitespace-nowrap"
            >
                {{ assignee.name }}
                <font-awesome-icon widthAuto :icon="faComment" :class="[active ? 'text-sky-500' : 'text-slate-500']" />
            </vx-badge>

            <vx-popover v-if="showGroupBadge" hover placement="top" arrow>
                <vx-badge :color="active ? 'primary' : 'slate'" size="xs">
                    <span class="line-clamp-1">{{ group?.name || props.thread.group_id }}</span>
                    <font-awesome-icon widthAuto :icon="faGrid2Plus" class="text-slate-500"></font-awesome-icon>
                </vx-badge>
                <template v-if="group" v-slot:content>
                    <div class="text-xs">{{ group.name }}</div>
                </template>
            </vx-popover>
        </template>
    </message-hub-sidebar-row>
</template>

<script setup>
import { faComment, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxPopover } from '@voxie/frontend-components';
import dayjs from '~/utils/dayjs';
import { computed, inject, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import MessageHubSidebarRow from '~/components/message-hub/message-hub-sidebar/MessageHubSidebarRow.vue';
import { formatPhoneNumber } from '~/components/filters';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { getClientTz } from '~/utils/date';
import { useInterval } from '@vueuse/core';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const route = useRoute();

const FEATURES = inject('FEATURES');

const props = defineProps({
    thread: {
        type: Object,
        required: true,
    },
    bulkSelection: {
        type: Boolean,
        default: false,
    },
});

const { currentOrgUnit } = useCurrentGroup();

const { agents, groups, isThreadSelected, currentInbox } = useMessageHub();

const assignee = computed(() =>
    FEATURES.simple_message_hub ? undefined : agents.value.find((agent) => props.thread.assignee_id === agent.id)
);
const unread = computed(() => (FEATURES.simple_message_hub ? props.thread.status === 'open' : props.thread.unread > 0));
const active = computed(() => {
    if (Number(route.params.contactId) !== props.thread.contact_id) {
        return false;
    }
    if (currentOrgUnit.value && props.thread.group_id) {
        return String(route.query.group) === String(props.thread.group_id);
    }
    return true;
});

const messageDate = computed(() => dayjs.utc(props.thread.last_message_at).tz(getClientTz()));

const formattedDate = () => {
    if (messageDate.value.isYesterday()) {
        return 'Yesterday';
    }

    return messageDate.value.isToday() ? messageDate.value.fromNow() : messageDate.value.format('MM/DD/YYYY');
};

const { counter: dateRefreshKey, pause: pauseDateRefreshKey } = useInterval(6000, {
    controls: true,
});

const showGroupBadge = computed(() => currentOrgUnit.value && props.thread.group_id);
const group = computed(() => {
    if (!showGroupBadge.value) {
        return;
    }

    return groups.value.find((group) => String(group.group_id) === String(props.thread.group_id));
});

onMounted(() => {
    if (!messageDate.value.isToday()) {
        pauseDateRefreshKey();
    }
});
</script>
